import { getLocaleFirstDayOfWeek } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injectable,
  input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  output,
  Output,
  ViewChild,
} from '@angular/core';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarType } from '@modules/calendar-app/types/calendar-type';
import { DateFormatService } from '@modules/common/services/date-format.service';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { refreshOnDisplayZoneChange } from '@modules/full-calendar/types/display-zone-refresh';
import { toDisplayZoneWallClock } from '@modules/full-calendar/types/display-timezone-format';
import { DragData, DragDataTypes } from '@modules/drag-n-drop/types/drag-data';
import { AngularCalendarHelper } from '@modules/full-calendar/types/AngularCalendarHelper';
import { CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { GroupBy } from '@sharedlib/common/lang/GroupBy';
import {
  CalendarDateFormatter,
  CalendarEventTimesChangedEvent,
  CalendarEventTimesChangedEventType,
  CalendarWeekViewComponent,
  DateAdapter,
  DateFormatterParams,
  type CalendarEvent as AngularCalendarEvent,
} from 'angular-calendar';
import { type WeekViewHourColumn } from 'calendar-utils';
import { Subject } from 'rxjs';

@Injectable()
class CustomDateFormatter extends CalendarDateFormatter {
  private readonly dateFormatService = inject(DateFormatService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  // SBUX-829 N1: the day-of-week / day-number formatters are date-only (browser-local), so render the
  // column date's DISPLAY-zone wall clock — otherwise, when the account zone is east of the browser,
  // the headers show the previous day and disagree with the grid geometry + events list.
  public weekViewColumnHeader({ date }: DateFormatterParams): string {
    return this.dateFormatService.formatDayOfWeekShort(
      toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone()),
    );
  }

  public weekViewHour({ date }: DateFormatterParams): string {
    // formatTimeShort already resolves to the display zone.
    return this.dateFormatService.formatTimeShort(date);
  }

  public weekViewColumnSubHeader({ date }: DateFormatterParams): string {
    return this.dateFormatService.formatDayNumber(toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone()));
  }
}

interface DayGroupedEvents {
  readonly events: AngularCalendarEvent<CalendarEventSummaryDto>[];
  readonly collapsedEvents: AngularCalendarEvent<CalendarEventSummaryDto>[];
}

@Component({
  selector: 'stch-calendar-week',
  styleUrls: ['./calendar-week.component.less'],
  templateUrl: './calendar-week.component.html',
  providers: [{ provide: CalendarDateFormatter, useClass: CustomDateFormatter }],
  standalone: false,
})
export class CalendarWeekComponent implements OnInit, OnDestroy {
  protected readonly MAX_ALL_DAY_EVENTS_COLLAPSED = 3;
  protected readonly LOCALE_ID = inject(LOCALE_ID);

  public readonly events = input.required<CalendarEventSummaryDto[]>();

  // SBUX-829: grid geometry runs in the app-wide display timezone via the DateAdapter (provided per
  // space context) — events are positioned in that zone, so this component needs no per-event shifting.
  //
  // Emitting is NOT symmetric with that, and the distinction matters:
  //  - CREATE paths (`clickHourSegment`, `handleDrop`) emit a display-zone WALL CLOCK, because the event
  //    form decodes in the event's own zone — see `toDisplayZoneSlot`.
  //  - MOVE/RESIZE and the day-number emits pass the real INSTANT through, because their consumers
  //    (`bunchUpdate`, `scrollToDateHandler`) work in instants.
  private readonly dateAdapter = inject(DateAdapter);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  protected readonly calendarEvents = computed<AngularCalendarEvent<CalendarEventSummaryDto>[]>(() => {
    // Position all-day events in the display zone too (reactive to zone changes) — otherwise they
    // bucket a day early when the account zone differs from the browser zone (SBUX-829 H1).
    const displayTimeZone = this.displayTimeZoneService.timeZone();
    return this.events().map((event) => CalendarEvent.makeAngularCalendarEvent(event, displayTimeZone));
  });

  protected readonly eventClicked = output<CalendarEventSummaryDto>();
  protected readonly eventDblClicked = output<CalendarEventSummaryDto>();

  public readonly viewDate = input.required<Date>();
  public readonly scrollToDate = input<Date>();
  public readonly calendarType = input(CalendarType.WEEK);
  /** The day name and number above each column; off where a date row above already names the day. */
  public readonly showDayHeader = input(true);

  protected readonly firstDayOfWeek = computed<number>(() => getLocaleFirstDayOfWeek(this.LOCALE_ID));

  @Output() dateClicked = new EventEmitter<CalendarCellClickEvent>();
  @Output() dateDblClicked = new EventEmitter<Date>();
  @Output() dayNumberClicked = new EventEmitter<Date>();
  @Output() dayNumberDblClicked = new EventEmitter<Date>();
  @Output() eventDropped = new EventEmitter<CalendarDropEvent>();

  protected readonly excludeDays = computed<number[]>(() => {
    const calendarType = this.calendarType();
    const viewDate = this.viewDate();

    switch (calendarType) {
      case CalendarType.WEEK:
        return [];
      case CalendarType.WORKWEEK:
        return [0, 6];
      case CalendarType.DAY: {
        // SBUX-829: `calendar-utils` filters columns with the display-zone DateAdapter's getDay(),
        // so the day we keep must be read in that same zone. A browser-local getDay() on an
        // account-zone instant shows the neighbouring day's column under today's heading.
        const displayDay = toDisplayZoneWallClock(
          viewDate || new Date(),
          this.displayTimeZoneService.timeZone(),
        ).getDay();
        return new Array(7)
          .fill(null)
          .map((_, index) => index)
          .filter((day) => day !== displayDay);
      }
      default:
        console.warn(`Unsupported calendar type: ${calendarType}`);
        return [];
    }
  });

  // Also re-lays the grid out when the display zone arrives (see the helper's TSDoc).
  protected readonly refresh = refreshOnDisplayZoneChange(new Subject<void>());
  public timezoneOffset: string;
  public allDayCollapsed = true;

  private readonly alive = new Subject<void>();

  @ViewChild('currentTimeMarker')
  protected readonly currentTimeMarker: ElementRef<HTMLElement>;

  @ViewChild(CalendarWeekViewComponent, { static: true })
  protected readonly mwlCalendarWeekView: CalendarWeekViewComponent;

  @ViewChild(CalendarWeekViewComponent, { static: true, read: ElementRef })
  protected readonly mwlCalendarWeekViewElement: ElementRef<HTMLElement>;

  private readonly scrollToDateEffect = effect(() => {
    const scrollToDate = this.scrollToDate();
    setTimeout(() => this.scrollToDateHandler(scrollToDate));
  });

  private readonly _allDayCollapsedEffect = effect(() => {
    this.calendarEvents(); // make it as dependency for this effect
    this.allDayCollapsed = true;
  });

  protected readonly groupedAllDayEvents = computed(() => {
    const allDayGrouped = GroupBy.toObject<AngularCalendarEvent<CalendarEventSummaryDto>>()
      .byExtractor((e) => this.getDayDateTime(e.meta.startTime))
      .build();
    allDayGrouped.consumeAll(this.calendarEvents().filter(({ allDay }) => allDay));

    return Object.fromEntries(
      Object.entries(allDayGrouped.result).map(([key, events]) => {
        const day: DayGroupedEvents = {
          events: events,
          collapsedEvents: events.slice(0, this.MAX_ALL_DAY_EVENTS_COLLAPSED),
        };
        return [key, day];
      }),
    );
  });

  protected readonly hasExtraAllDayEvents = computed(() =>
    Object.values(this.groupedAllDayEvents()).some((g) => g.events.length !== g.collapsedEvents.length),
  );

  protected getDayDateTime(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }

  public ngOnInit() {
    this.getCurrentTimezone();
  }

  /**
   * A click on a day's header acts on the first click, at once. The browser counts the clicks of a gesture itself:
   * the second click of a double-click (`detail` 2) is the double-click. Read from the click rather than from a
   * `dblclick` listener because the first click may already have changed the view (Day view re-renders the
   * header), and the browser still counts the second click on whatever is under the pointer then.
   */
  protected handleDayHeaderClick(date: Date, sourceEvent: MouseEvent): void {
    const clicks = sourceEvent?.detail ?? 0;
    if (clicks <= 1) {
      this.dayNumberClicked.emit(date);
    } else if (clicks === 2) {
      this.dayNumberDblClicked.emit(date);
    }
  }

  public ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  protected toggleAllDay() {
    this.allDayCollapsed = !this.allDayCollapsed;
  }

  private getCurrentTimezone() {
    const offsetMinutes = new Date().getTimezoneOffset();
    const offsetHours = Math.abs(offsetMinutes / 60);
    const offsetSign = offsetMinutes < 0 ? '+' : '-';

    this.timezoneOffset = `${offsetSign} ${this.padZero(offsetHours)}:${this.padZero(offsetMinutes % 60)}`;
  }

  private padZero(num: number): string {
    return num < 10 ? '0' + num : num.toString();
  }

  /**
   * The clicked slot as a DISPLAY-ZONE WALL CLOCK, for the CREATE paths only (SBUX-829).
   *
   * mwl builds the grid with the account-zone `DateAdapter`, so a slot's raw value is the INSTANT of
   * that account-zone moment. Emitting it as-is was wrong because the consumer decodes in a different
   * frame: `CalendarEvent.fromCalendarCell` hands it to the event form, which seeds in the EVENT's own
   * zone (the calendar's). With the account in Kyiv and the calendar in New York, clicking the column
   * headed "Mon 27" emitted `2026-07-26T21:00Z` — Kyiv's Mon 27 00:00 — which the form read as Jul 26
   * 17:00 New York and showed as **Jul 26**, one day early. The Repeat presets then inherited it and
   * offered "Weekly On Sunday" for a Monday cell.
   *
   * Converting to a wall clock keeps the NUMBERS the user clicked, so the event lands on Mon 27 00:00
   * in whatever zone it ends up in — the same convention month/year already use (`toDisplayZoneDay`,
   * the C1 fix) and the same keep-the-numbers rule the pickers follow. This component previously only
   * corrected the frame for DISPLAY (headers, day-column filtering), never for what it emitted.
   *
   * Deliberately NOT applied to `handleMove` (drag/resize of an EXISTING event) or to the day-number
   * emits: those carry real instants onward — `bunchUpdate` persists a timed event's instant verbatim,
   * and `scrollToDateHandler` matches against the grid's own account-frame dates. A carrier there would
   * break them.
   */
  private toDisplayZoneSlot(date: Date): Date {
    return toDisplayZoneWallClock(date, this.displayTimeZoneService.timeZone());
  }

  protected clickHourSegment(event: MouseEvent, date: Date, origin: HTMLElement) {
    this.dateClicked.emit({ date: this.toDisplayZoneSlot(date), originRef: new ElementRef(origin), event });
  }

  /**
   * Double-click on a slot — the SAME frame as the single click, so both create the same event.
   *
   * SBUX-829: the template used to emit `segment.date` (mwl's raw account-frame instant) straight out,
   * while every other emit path on this grid sends an account-zone wall clock. The host then converted it
   * as though it were a wall clock, so a double-click stored a time shifted by browser−account: clicking
   * the 10:00 slot with the account in Kyiv on a New York device persisted 03:00. Confirmed end-to-end by
   * reading the POST payload, in both an east- and a west-of-account browser.
   */
  protected dblClickHourSegment(date: Date): void {
    this.dateDblClicked.emit(this.toDisplayZoneSlot(date));
  }

  protected handleDrop(dragData: DragData, newStart: Date, origin: HTMLElement) {
    // Same frame as the click path: this creates a NEW event from an external item, so the dropped slot
    // must arrive as the wall clock that was dropped on. `newEnd` is then derived with browser-local
    // getters, which read the carrier's wall clock — so +1h stays +1h in the account frame.
    const slot = this.toDisplayZoneSlot(newStart);
    this.eventDropped.emit({
      dragData,
      newStart: slot,
      newEnd: new Date(slot.getFullYear(), slot.getMonth(), slot.getDate(), slot.getHours() + 1, slot.getMinutes()),
      originRef: new ElementRef(origin),
    });
  }

  protected handleMove({ type, event, newStart, newEnd }: CalendarEventTimesChangedEvent) {
    if (type === CalendarEventTimesChangedEventType.Resize || type === CalendarEventTimesChangedEventType.Drag) {
      // mwl's own view state works in the grid's (account-zone) instants — hand it those, unconverted.
      event.start = newStart;
      event.end = newEnd;
      this.mwlCalendarWeekView.refresh.next(void 0);
    }

    // SBUX-829: emit the SAME kind of value as `clickHourSegment`/`handleDrop` above — an account-zone
    // wall clock. `eventDropped` is one output with one contract, and it previously carried a raw instant
    // from here and a carrier from there, so no single consumer could be right about both: whichever frame
    // `resolveDropTimes` was told about, the other path was being converted wrongly (or not at all).
    this.eventDropped.emit({
      dragData: {
        type: DragDataTypes.event,
        data: [event.meta],
      },
      newStart: this.toDisplayZoneSlot(newStart),
      newEnd: this.toDisplayZoneSlot(newEnd),
    });
  }

  private findSegmentIndex(date: Date, column: WeekViewHourColumn): number | undefined {
    let foundSegmentIndex = -1;
    // Index by the display-zone hour (the DateAdapter builds hourColumns in the display zone).
    const hour = column.hours[this.dateAdapter.getHours(date)];
    for (let segmentIndex = 0; segmentIndex < hour.segments.length; segmentIndex++) {
      const segment = hour.segments[segmentIndex];
      if (segment.date.getTime() <= date.getTime()) {
        foundSegmentIndex = segmentIndex;
      } else {
        break;
      }
    }
    return foundSegmentIndex;
  }

  public scrollToDateHandler(date: Date): boolean {
    if (date) {
      const dayIndex: number | undefined = AngularCalendarHelper.findDayIndex(date, this.mwlCalendarWeekView.days);
      const column = this.mwlCalendarWeekView.view.hourColumns[dayIndex];
      if (!column) {
        return false;
      }

      const foundSegmentIndex = this.findSegmentIndex(date, column);
      if (foundSegmentIndex === undefined) {
        return false;
      }

      const dayColumn = this.mwlCalendarWeekViewElement.nativeElement
        .querySelectorAll('.cal-time-events .cal-day-column')
        .item(dayIndex);
      // Index the hour cell by the display-zone hour (grid is laid out in the display zone).
      const hourCell = dayColumn.querySelectorAll('.cal-hour').item(this.dateAdapter.getHours(date));
      const segmentCell = hourCell.querySelectorAll('.cal-hour-segment').item(foundSegmentIndex);
      segmentCell?.scrollIntoView({ behavior: 'smooth' });
      return true;
    }

    return false;
  }
}
